// signalChainCalculator.js - Signal Chain Analysis

import RFCalc from './rfCalculations';
import { interpolateS21 } from './s2pInterpolator';
import { interpolateParameter } from './freqDataInterpolator';

export class SignalChainCalculator {
  constructor(nodes, edges, options = {}) {
    this.nodes = nodes;
    this.edges = edges;
    this.currentFrequency = options.currentFrequency || null;
    this.s2pCache = options.s2pCache || {};
    this.freqDataCache = options.freqDataCache || {};
  }

  /**
   * Find all signal paths starting from source nodes
   */
  findSignalPaths() {
    const sources = this.nodes.filter(n =>
      n.type === 'inputSource' || n.type === 'noiseSource'
    );

    const allPaths = [];

    sources.forEach(source => {
      const paths = this.traverseAllPaths(source.id, new Set());
      allPaths.push(...paths);
    });

    return allPaths;
  }

  /**
   * Traverse all signal paths recursively, handling multiple outputs
   * Returns an array of paths (each path is an array of nodes)
   */
  traverseAllPaths(nodeId, visited) {
    if (visited.has(nodeId)) return []; // Prevent loops

    const node = this.nodes.find(n => n.id === nodeId);
    if (!node) return [];

    // Create new visited set for this branch
    const newVisited = new Set(visited);
    newVisited.add(nodeId);

    // Find outgoing edges
    const outEdges = this.edges.filter(e => e.source === nodeId);

    if (outEdges.length === 0) {
      // End of path - return single path with just this node
      return [[node]];
    }

    // Handle multiple outputs - traverse all branches
    const allPaths = [];

    outEdges.forEach(edge => {
      const childPaths = this.traverseAllPaths(edge.target, newVisited);
      // Prepend current node to each child path
      childPaths.forEach(childPath => {
        allPaths.push([node, ...childPath]);
      });
    });

    return allPaths;
  }

  /**
   * Get component gain (handles attenuators as negative gain, data sources)
   */
  getGain(node, frequency = null) {
    const params = node.data?.parameters || {};
    const freq = frequency || this.currentFrequency;

    // Check for gain parameter with data source support
    if (params.gain !== undefined) {
      const dataSource = params.gainDataSource?.value || 'manual';

      // Priority 1: S2P data (if enabled)
      if (dataSource === 's2p' && freq !== null) {
        // Check cache first
        if (this.s2pCache[node.id]?.[freq] !== undefined) {
          return this.s2pCache[node.id][freq].gain;
        }

        // Fall back to direct interpolation
        const s2pData = params.s2pData?.value;
        if (s2pData && s2pData.frequencies && s2pData.s21_mag_db) {
          const result = interpolateS21(s2pData, freq);
          return result.gain;
        }
      }

      // Priority 2: Frequency database (if enabled)
      if (dataSource === 'database' && freq !== null) {
        // Check cache first
        if (this.freqDataCache[node.id]?.[freq] !== undefined) {
          return this.freqDataCache[node.id][freq].gain;
        }

        // Fall back to direct interpolation
        const freqData = params.freqDbData?.value;
        if (freqData && Array.isArray(freqData) && freqData.length > 0) {
          const result = interpolateParameter(freqData, 'gain_db', freq);
          return result.value;
        }
      }

      // Priority 3: Manual value
      return params.gain.value || 0;
    }

    if (params.attenuation !== undefined) {
      return -(params.attenuation.value || 0);
    }

    if (params.conversionLoss !== undefined) {
      return -(params.conversionLoss.value || 0);
    }

    if (params.conversionGain !== undefined) {
      return params.conversionGain.value || 0;
    }

    if (params.insertionLoss !== undefined) {
      const baseInsertionLoss = params.insertionLoss.value || 0;
      // Apply frequency-dependent model for filters
      if (freq !== null && (node.type === 'bandpassFilter' || node.type === 'lowpassFilter' ||
          node.type === 'highpassFilter' || node.type === 'bandstopFilter')) {
        return this.getFrequencyDependentValue(node, 'insertionLoss', baseInsertionLoss, freq);
      }
      return -baseInsertionLoss;
    }

    // Splitter loss per port
    if (params.lossPerPort !== undefined) {
      return -(params.lossPerPort.value || 0);
    }

    // Cable
    if (params.length !== undefined && params.lossPerMeter !== undefined) {
      return -RFCalc.cableLoss(params.length.value, params.lossPerMeter.value);
    }

    return 0;
  }

  /**
   * Get component noise figure (with data source support)
   * For passive components: NF = Loss (in dB)
   */
  getNoiseFigure(node, frequency = null) {
    const params = node.data?.parameters || {};
    const freq = frequency || this.currentFrequency;

    if (params.noiseFigure !== undefined) {
      const dataSource = params.nfDataSource?.value || 'manual';

      // Frequency database (S2P doesn't provide NF)
      if (dataSource === 'database' && freq !== null) {
        // Check cache first
        if (this.freqDataCache[node.id]?.[freq] !== undefined) {
          return this.freqDataCache[node.id][freq].nf;
        }

        // Fall back to direct interpolation
        const freqData = params.freqDbData?.value;
        if (freqData && Array.isArray(freqData) && freqData.length > 0) {
          const result = interpolateParameter(freqData, 'nf_db', freq);
          return result.value;
        }
      }

      // Manual value
      return params.noiseFigure.value || 0;
    }

    // For passive components, NF equals loss
    const gain = this.getGain(node, freq);
    if (gain < 0) {
      return Math.abs(gain);
    }

    return 0;
  }

  /**
   * Get component P1dB (with data source support)
   */
  getP1dB(node, frequency = null) {
    const params = node.data?.parameters || {};
    const freq = frequency || this.currentFrequency;

    if (params.p1db !== undefined) {
      const dataSource = params.p1dbDataSource?.value || 'manual';

      // Frequency database
      if (dataSource === 'database' && freq !== null) {
        // Check cache first
        if (this.freqDataCache[node.id]?.[freq] !== undefined) {
          return this.freqDataCache[node.id][freq].p1db;
        }

        // Fall back to direct interpolation
        const freqData = params.freqDbData?.value;
        if (freqData && Array.isArray(freqData) && freqData.length > 0) {
          const result = interpolateParameter(freqData, 'p1db_dbm', freq);
          return result.value;
        }
      }

      // Manual value
      return params.p1db.value || Infinity;
    }

    return Infinity;
  }

  /**
   * Get component OIP3 (with data source support)
   */
  getOIP3(node, frequency = null) {
    const params = node.data?.parameters || {};
    const freq = frequency || this.currentFrequency;

    if (params.oip3 !== undefined) {
      const dataSource = params.oip3DataSource?.value || 'manual';

      // Frequency database
      if (dataSource === 'database' && freq !== null) {
        // Check cache first
        if (this.freqDataCache[node.id]?.[freq] !== undefined) {
          return this.freqDataCache[node.id][freq].oip3;
        }

        // Fall back to direct interpolation
        const freqData = params.freqDbData?.value;
        if (freqData && Array.isArray(freqData) && freqData.length > 0) {
          const result = interpolateParameter(freqData, 'oip3_dbm', freq);
          return result.value;
        }
      }

      // Manual value
      return params.oip3.value || Infinity;
    }

    return Infinity;
  }

  /**
   * Get source power
   */
  getSourcePower(node) {
    const params = node.data?.parameters || {};
    return params.power?.value || 0;
  }

  /**
   * Calculate entire signal chain
   * Now handles multiple paths from splitters/switches
   */
  calculateChain() {
    const paths = this.findSignalPaths();
    const results = {};

    paths.forEach((path, pathIndex) => {
      if (path.length === 0) return;

      let cumulativeGain = 0; // dB
      let inputPower = this.getSourcePower(path[0]); // dBm
      let frequency = this.currentFrequency || path[0].data?.parameters?.frequency?.value; // MHz

      const stages = [];

      for (let i = 0; i < path.length; i++) {
        const node = path[i];

        // Get component parameters (with frequency for data source interpolation)
        const gain = this.getGain(node, frequency);
        const nf = this.getNoiseFigure(node, frequency);
        const p1db = this.getP1dB(node, frequency);
        const oip3 = this.getOIP3(node, frequency);

        // Calculate output power for this stage
        let outputPower;

        // Special handling for Combiner components - sum both input powers
        if (node.type === 'combiner') {
          // Find all incoming edges to this combiner
          const incomingEdges = this.edges.filter(edge => edge.target === node.id);

          if (incomingEdges.length >= 2) {
            // Get output power from both source nodes
            const inputPowers = incomingEdges.map(edge => {
              const sourceNode = this.nodes.find(n => n.id === edge.source);
              // Get the calculated output power from results, or use current inputPower
              return results[sourceNode?.id]?.outputPower
                ? parseFloat(results[sourceNode.id].outputPower)
                : inputPower;
            });

            // Convert both powers from dBm to watts, sum, then back to dBm
            const watts1 = RFCalc.dbmToWatts(inputPowers[0]);
            const watts2 = RFCalc.dbmToWatts(inputPowers[1]);
            const totalWatts = watts1 + watts2;
            const combinedPowerDbm = RFCalc.wattsToDbm(totalWatts);

            // Apply combiner loss (gain is negative for passive loss)
            outputPower = combinedPowerDbm + gain;
          } else {
            // Only one input connected, treat as normal
            outputPower = inputPower + gain;
          }
        } else {
          // Normal calculation for non-combiner components
          outputPower = inputPower + gain;
        }

        // Calculate compression for this stage (before saturation limiting)
        const compression = RFCalc.calculateCompression(inputPower, gain, p1db);
        const compressionState = RFCalc.getCompressionState(outputPower, p1db);

        // Apply saturation limit (P1dB + 2dB max)
        const limitedOutputPower = RFCalc.limitToSaturation(outputPower, p1db);

        // Accumulate cumulative gain
        cumulativeGain += gain;

        // Add to stages array for cascaded calculations
        if (node.type !== 'inputSource' && node.type !== 'noiseSource') {
          stages.push({ gain, nf, oip3, p1db });
        }

        // Calculate cascaded metrics up to this point
        const cumulativeNF = stages.length > 0 ? RFCalc.cascadeNoiseFigure(stages) : 0;
        const cumulativeOIP3 = stages.length > 0 ? RFCalc.cascadeIP3(stages) : Infinity;
        const cumulativeP1dB = stages.length > 0 ? RFCalc.cascadeP1dB(stages) : Infinity;
        const cumulativeIIP3 = isFinite(cumulativeOIP3) ?
          RFCalc.oip3ToIip3(cumulativeOIP3, cumulativeGain) : Infinity;

        // Store results for this node
        // If node already has results (from another path), keep existing
        // This handles cases where a splitter feeds multiple branches
        if (!results[node.id]) {
          // Determine if this is the first gain component in the chain
          const isFirstGainComponent = stages.length === 1;

          results[node.id] = {
            inputPower: inputPower.toFixed(2),
            outputPower: limitedOutputPower.toFixed(2),
            gain: gain.toFixed(2),
            cumulativeGain: cumulativeGain.toFixed(2),
            cumulativeNF: cumulativeNF.toFixed(2),
            noiseFigure: nf.toFixed(2),
            compression: compression.toFixed(3),
            compressionState,
            p1db: isFinite(cumulativeP1dB) ? cumulativeP1dB.toFixed(1) : 'N/A',
            oip3: isFinite(oip3) ? oip3.toFixed(1) : 'N/A',
            iip3: isFinite(oip3) ? RFCalc.oip3ToIip3(oip3, gain).toFixed(1) : 'N/A',
            cumulativeOIP3: isFinite(cumulativeOIP3) ? cumulativeOIP3.toFixed(1) : 'N/A',
            cumulativeIIP3: isFinite(cumulativeIIP3) ? cumulativeIIP3.toFixed(1) : 'N/A',
            frequency: frequency.toFixed(1),
            isFirstGainComponent
          };
        }

        // Set input power for next stage (use limited output)
        inputPower = limitedOutputPower;
      }

      // Calculate system-level metrics for the entire path
      if (stages.length > 0) {
        const systemNF = RFCalc.cascadeNoiseFigure(stages);
        const systemOIP3 = RFCalc.cascadeIP3(stages);
        const systemP1dB = RFCalc.cascadeP1dB(stages);

        results[`path_${pathIndex}`] = {
          totalGain: cumulativeGain.toFixed(2),
          noiseFigure: systemNF.toFixed(2),
          oip3: isFinite(systemOIP3) ? systemOIP3.toFixed(1) : 'N/A',
          p1db: isFinite(systemP1dB) ? systemP1dB.toFixed(1) : 'N/A',
          outputPower: inputPower.toFixed(2),
          frequency: frequency.toFixed(1)
        };
      }
    });

    return results;
  }

  /**
   * Calculate frequency-dependent parameter value
   * Supports basic filter models and cable frequency dependence
   */
  getFrequencyDependentValue(node, paramName, baseValue, frequency) {
    const type = node.type;
    const params = node.data?.parameters || {};

    // Bandpass Filter - Gaussian response around center frequency
    if (type === 'bandpassFilter' && (paramName === 'gain' || paramName === 'insertionLoss')) {
      const centerFreq = params.centerFrequency?.value || 2400;
      const bandwidth = params.bandwidth?.value || 200;
      const insertionLoss = params.insertionLoss?.value || 1.0;

      // Calculate normalized frequency offset
      const deltaF = Math.abs(frequency - centerFreq);
      const halfBW = bandwidth / 2;

      // Gaussian roll-off outside passband
      if (deltaF < halfBW) {
        // Within passband - nominal insertion loss
        return -insertionLoss;
      } else {
        // Outside passband - add attenuation based on distance from passband
        const rolloff = Math.pow((deltaF - halfBW) / halfBW, 2);
        const additionalLoss = Math.min(50, 20 * rolloff); // Cap at -50dB
        return -(insertionLoss + additionalLoss);
      }
    }

    // Lowpass Filter - Roll-off above cutoff
    if (type === 'lowpassFilter' && (paramName === 'gain' || paramName === 'insertionLoss')) {
      const cutoffFreq = params.cutoffFrequency?.value || 2400;
      const insertionLoss = params.insertionLoss?.value || 1.0;

      if (frequency <= cutoffFreq) {
        return -insertionLoss;
      } else {
        // Simple first-order roll-off: 20dB/decade
        const decades = Math.log10(frequency / cutoffFreq);
        const rolloff = 20 * decades;
        const additionalLoss = Math.min(50, rolloff);
        return -(insertionLoss + additionalLoss);
      }
    }

    // Highpass Filter - Roll-off below cutoff
    if (type === 'highpassFilter' && (paramName === 'gain' || paramName === 'insertionLoss')) {
      const cutoffFreq = params.cutoffFrequency?.value || 2400;
      const insertionLoss = params.insertionLoss?.value || 1.0;

      if (frequency >= cutoffFreq) {
        return -insertionLoss;
      } else {
        // Simple first-order roll-off: 20dB/decade
        const decades = Math.log10(cutoffFreq / frequency);
        const rolloff = 20 * decades;
        const additionalLoss = Math.min(50, rolloff);
        return -(insertionLoss + additionalLoss);
      }
    }

    // Bandstop (Notch) Filter - Attenuation at center frequency
    if (type === 'bandstopFilter' && (paramName === 'gain' || paramName === 'insertionLoss')) {
      const centerFreq = params.centerFrequency?.value || 2400;
      const bandwidth = params.bandwidth?.value || 200;
      const insertionLoss = params.insertionLoss?.value || 1.0;

      const deltaF = Math.abs(frequency - centerFreq);
      const halfBW = bandwidth / 2;

      if (deltaF > halfBW) {
        // Outside notch - nominal insertion loss
        return -insertionLoss;
      } else {
        // Inside notch - add attenuation (max at center)
        const notchDepth = 30; // dB
        const attenuation = notchDepth * (1 - deltaF / halfBW);
        return -(insertionLoss + attenuation);
      }
    }

    // Cable - Loss increases with frequency (skin effect)
    if (type === 'cable' && paramName === 'lossPerMeter') {
      const baseLoss = baseValue;
      const baseFreq = 2400; // MHz - reference frequency

      // Loss increases proportional to sqrt(frequency) due to skin effect
      const frequencyFactor = Math.sqrt(frequency / baseFreq);
      return baseLoss * frequencyFactor;
    }

    // Amplifier - Gain roll-off at high frequencies (simple model)
    if ((type === 'lna' || type === 'powerAmplifier' || type === 'vga') && paramName === 'gain') {
      // Simple -3dB point model (could be enhanced with component specs)
      const nominalGain = baseValue;
      const f3dB = 10000; // MHz - assume 10 GHz bandwidth for now

      if (frequency < f3dB * 0.5) {
        return nominalGain;
      } else {
        // Roll-off above half the 3dB frequency
        const rolloff = 20 * Math.log10(1 + Math.pow(frequency / f3dB, 2));
        return nominalGain - rolloff;
      }
    }

    // Default: return base value (frequency-independent)
    return baseValue;
  }

  /**
   * Create frequency-adjusted node for sweep calculations
   */
  createFrequencyAdjustedNode(node, frequency) {
    const adjustedNode = JSON.parse(JSON.stringify(node)); // Deep clone
    const params = adjustedNode.data?.parameters || {};

    // Update Signal Generator frequency to sweep frequency
    if (node.type === 'inputSource' && params.frequency) {
      params.frequency.value = frequency;
    }

    // Handle S2P data source for gain
    const s2pData = params.s2pData?.value;
    // DEBUG: Log S2P state in sweep (only first frequency point to avoid spam)
    if (params.gainDataSource?.value === 's2p' && frequency < 100) {
      const sweepDebug = {
        source: 'createFrequencyAdjustedNode',
        nodeType: node.type,
        gainDataSource: params.gainDataSource?.value,
        s2pDataExists: !!s2pData,
        hasFrequencies: !!s2pData?.frequencies,
        hasS21: !!s2pData?.s21_mag_db,
        frequency
      };
      console.log('[createFrequencyAdjustedNode] S2P check:', sweepDebug);
    }
    if (params.gainDataSource?.value === 's2p' && s2pData?.frequencies && s2pData?.s21_mag_db) {
      const result = interpolateS21(s2pData, frequency);
      if (result && result.gain !== null && result.gain !== undefined) {
        params.gain.value = result.gain;
      } else {
        adjustedNode.data._noDataAtFrequency = true;
      }
    }

    // Check if node has database frequency data for interpolation
    const freqData = params.freqDbData?.value;
    const hasFreqData = freqData && Array.isArray(freqData) && freqData.length > 0;

    // For catalog components with database data, interpolate from database
    if (hasFreqData) {
      const dbParamMap = {
        gain: { dbColumn: 'gain_db', sourceKey: 'gainDataSource' },
        noiseFigure: { dbColumn: 'nf_db', sourceKey: 'nfDataSource' },
        p1db: { dbColumn: 'p1db_dbm', sourceKey: 'p1dbDataSource' },
        oip3: { dbColumn: 'oip3_dbm', sourceKey: 'oip3DataSource' }
      };

      for (const [paramKey, config] of Object.entries(dbParamMap)) {
        const dataSource = params[config.sourceKey]?.value;
        if (dataSource === 'database' && params[paramKey]) {
          const result = interpolateParameter(freqData, config.dbColumn, frequency);
          if (result && result.value !== null && result.value !== undefined) {
            params[paramKey].value = result.value;
          } else if (result && result.value === null) {
            // Out of range - flag this node as having no data at this frequency
            adjustedNode.data._noDataAtFrequency = true;
          }
        }
      }
    }

    // Adjust gain-related parameters using mathematical models (for manual sources only)
    const gainSource = params.gainDataSource?.value;
    if (params.gain && gainSource !== 'database' && gainSource !== 's2p') {
      params.gain.value = this.getFrequencyDependentValue(node, 'gain', params.gain.value, frequency);
    }
    if (params.insertionLoss) {
      params.insertionLoss.value = Math.abs(this.getFrequencyDependentValue(node, 'insertionLoss', params.insertionLoss.value, frequency));
    }
    if (params.lossPerMeter) {
      params.lossPerMeter.value = this.getFrequencyDependentValue(node, 'lossPerMeter', params.lossPerMeter.value, frequency);
    }
    if (params.attenuation) {
      params.attenuation.value = Math.abs(this.getFrequencyDependentValue(node, 'attenuation', params.attenuation.value, frequency));
    }

    return adjustedNode;
  }

  /**
   * Perform frequency sweep analysis with frequency-dependent parameters
   */
  performFrequencySweep(startFreq, stopFreq, numPoints) {
    const frequencies = [];
    const step = (stopFreq - startFreq) / (numPoints - 1);

    for (let i = 0; i < numPoints; i++) {
      frequencies.push(startFreq + i * step);
    }

    const sweepResults = {
      frequencies,
      gain: [],
      noiseFigure: [],
      outputPower: [],
      p1db: [],
      oip3: []
    };

    frequencies.forEach(freq => {
      // Create frequency-adjusted nodes for this frequency point
      const adjustedNodes = this.nodes.map(node => this.createFrequencyAdjustedNode(node, freq));

      // Check if any node lacks data at this frequency
      const hasNoData = adjustedNodes.some(node => node.data?._noDataAtFrequency);

      if (hasNoData) {
        // Push nulls for all metrics when any component lacks data
        sweepResults.gain.push(null);
        sweepResults.noiseFigure.push(null);
        sweepResults.outputPower.push(null);
        sweepResults.p1db.push(null);
        sweepResults.oip3.push(null);
        return;
      }

      // Create a temporary calculator with adjusted nodes
      const tempCalculator = new SignalChainCalculator(adjustedNodes, this.edges);
      const chainResults = tempCalculator.calculateChain();
      const pathResults = chainResults['path_0'];

      if (pathResults) {
        sweepResults.gain.push(parseFloat(pathResults.totalGain));
        sweepResults.noiseFigure.push(parseFloat(pathResults.noiseFigure));
        sweepResults.outputPower.push(parseFloat(pathResults.outputPower));
        sweepResults.p1db.push(pathResults.p1db !== 'N/A' ? parseFloat(pathResults.p1db) : null);
        sweepResults.oip3.push(pathResults.oip3 !== 'N/A' ? parseFloat(pathResults.oip3) : null);
      } else {
        sweepResults.gain.push(null);
        sweepResults.noiseFigure.push(null);
        sweepResults.outputPower.push(null);
        sweepResults.p1db.push(null);
        sweepResults.oip3.push(null);
      }
    });

    return sweepResults;
  }
}

export default SignalChainCalculator;
